import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "store/actions"
import { Card, CardBody, CardTitle, Col, Container, Row } from "reactstrap"
import axios from "axios"
import config from "config/config"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import SnackbarComponent from "components/SnackBar"
import CampaignManagementTable from "./CampaignManagementTable"
import AudienceCard from "./AudienceCard"
import SurveyCard from "./SurveyCard"
import DonutChart from "components/Common/Charts/DonutChart"
import LineChart from "components/Common/Charts/LineChart"
import Knob from "pages/AllCharts/knob/knob"
import Miniwidgets from "./MiniWidgets"
import Header from "./Header"
import SurveyDashboard from "newcomponents/survey-chart"
import SentimentAnalysisChart from "./SentimentAnalysisChart"
import SentimentAnalysisCard from "./SentimentAnalysisChart"
import PerformanceMetricsChart from "newcomponents/category-score"

const Surveys = props => {
  const [err, setErr] = useState(false)
  const [success, setSuccess] = useState(false)
  const [audience, setAudience] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [surveys, setSurveys] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reports, setReports] = useState([])
  const [avgSentiment, setAvgSentiment] = useState(null)
  const organizations = useSelector(state => state.organizationReducer)
  const navigate = useNavigate()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [responseStats, setResponseStats] = useState({
    totalSent: 0,
    totalOpened: 0,
    totalResponses: 0,
  })

  const fetchAudience = async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/audience/getOrganizationAudience/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      setAudience(response.data.audiences)
      setLoading(false)
    } catch (err) {
      console.log(err)
      setErr("Error fetching audiences.")
    }
  }

  const [categoryScores, setCategoryScores] = useState([])

  const fetchCategoryScores = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/organization/getOrganizationCategoryScore/${activeOrganization.id}`,
        {
          headers: { Authorization: `Bearer ${userData.token}` },
          params: { viewMode: "Month", fromDate: "", toDate: "" },
        },
      )
      setCategoryScores(response.data.categoryScores)
    } catch (err) {
      console.error("Error fetching category scores:", err)
    }
  }

  const fetchSurveys = async () => {
    try {
      const surveyResponse = await axios.get(
        `${config.backendUrl}/survey/getOrganizationSurveys/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      setSurveys(surveyResponse.data.surveys)
    } catch (err) {
      console.log(err)
      setErr("Error fetching surveys.")
    }
  }

  const fetchCampaigns = async () => {
    try {
      const campaignResponse = await axios.get(
        `${config.backendUrl}/survey/getDistribution/?orgIds=${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setCampaigns(campaignResponse.data.campaigns)
      setAvgSentiment(campaignResponse.data.overallAverageSentiment)
      calculateResponseStats(campaignResponse.data.campaigns)
      extractSentimentData(campaignResponse.data.campaigns) // Extract sentiment scores
    } catch (err) {
      console.log("Error fetching campaigns", err)
      setErr("Error fetching campaigns")
    }
  }

  const [sentimentData, setSentimentData] = useState({
    labels: [],
    data: [],
  })

  // Extract sentiment scores over time
  const extractSentimentData = campaigns => {
    let labels = []
    let data = []

    campaigns.forEach(campaign => {
      if (campaign.campaignSentimentScore) {
        // Convert start and end dates to formatted strings
        const startDate = new Date(campaign.startDate).toLocaleDateString(
          "en-US",
          {
            month: "short",
            day: "numeric",
          },
        )

        const endDate = new Date(campaign.endDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })

        // Construct label as "March 4 - March 5"
        labels.push(`${startDate} - ${endDate}`)
        data.push(campaign.campaignSentimentScore)
      }
    })

    setSentimentData({ labels, data })
  }
  const calculateResponseStats = campaigns => {
    let totalSent = 0
    let totalOpened = 0
    let totalResponses = 0

    campaigns.forEach(campaign => {
      campaign.SurveyDistributions.forEach(distribution => {
        if (distribution.SurveyCustomerAssignments) {
          totalSent += distribution.SurveyCustomerAssignments.length
          totalOpened += distribution.SurveyCustomerAssignments.filter(
            assignment => assignment.Opened,
          ).length
          totalResponses += distribution.SurveyCustomerAssignments.filter(
            assignment => assignment.Responded,
          ).length
        }
      })
    })

    setResponseStats({ totalSent, totalOpened, totalResponses })
  }

  useEffect(() => {
    fetchAudience()
    fetchCampaigns()
    fetchSurveys()
    fetchCategoryScores()
  }, [])

  useEffect(() => {
    if (campaigns.length > 0) {
      const activeCampaigns = campaigns.filter(
        campaign => campaign.status === "active",
      ).length
      const pausedCampaigns = campaigns.filter(
        campaign => campaign.status === "paused",
      ).length

      setReports([
        {
          title: "Total Campaigns",
          iconClass: "account-group",
          total: `${campaigns.length}`,
          badgecolor: "info",
        },
        {
          title: "Active Campaigns",
          iconClass: "thumb-up",
          total: activeCampaigns,
          badgecolor: "success",
        },
        {
          title: "Paused Campaigns",
          iconClass: "pause-circle",
          total: pausedCampaigns,
          badgecolor: "warning",
        },
        {
          title: "Sentiment Score",
          iconClass: "star",
          total: avgSentiment ? avgSentiment.toFixed(2) : "N/A",
          badgecolor: "primary",
        },
      ])
    }
  }, [campaigns, avgSentiment])

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      {/* Donut Chart & Survey Stats */}
      <Miniwidgets noFilter reports={reports} loading={loading} />

      <Header
        err={err}
        setErr={setErr}
        success={success}
        setSuccess={setSuccess}
      />
      <Row>
        <Col xl="12">
          <SentimentAnalysisCard
            title="Campaign Sentiment Score"
            labels={sentimentData.labels}
            data={sentimentData.data}
            color="#ff5733"
            isLoading={loading}
          />
        </Col>
      </Row>
      <Row className="my-4">
        <Col xl="6">
          <SurveyDashboard />
        </Col>
        <Col xl="6">
          <PerformanceMetricsChart
            fromDate={null}
            toDate={null}
            viewMode={"Month"}
          />
        </Col>
      </Row>

      <Row>
        <Col xl="6" style={{ height: "75vh" }}>
          <AudienceCard
            audiences={audience}
            setErr={setErr}
            setSuccess={setSuccess}
          />
        </Col>
        <Col xl="6" style={{ height: "75vh" }}>
          <SurveyCard
            setErr={setErr}
            setSuccess={setSuccess}
            surveys={surveys}
          />
        </Col>
      </Row>

      {/* Campaigns Table */}
      <Row className="my-4">
        <Col xl="12" style={{ height: "75vh" }}>
          <CampaignManagementTable
            campaigns={campaigns}
            setErr={setErr}
            setSuccess={setSuccess}
          />
        </Col>
      </Row>

      {/* Audience and Survey Cards */}
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(Surveys)
